/**
 * Verify the code.
 *
 * Built to docs/references/07-verify-otp.png.
 *
 * Two details from the reference are doing real work:
 *
 *   "Waiting to automatically detect an SMS" — on a phone the code can be
 *   filled in by the platform. autoComplete="one-time-code" is what enables
 *   that, so the promise in the copy is kept rather than decorative.
 *
 *   The resend countdown — a code that can be re-requested instantly is a
 *   free SMS gun pointed at anyone's phone. The timer is the rate limit the
 *   user can see; the server enforces its own regardless.
 *
 * It is one input rather than six boxes. Six boxes look tidy and break
 * pasting, autofill and backspace in ways that are a genuine nuisance on a
 * screen people only reach when they are already mildly annoyed.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { formatDisplay, userMessage } from '@zivapp/shared'
import { useAuth } from '../context/index.js'
import ThemeToggle from '../components/ui/ThemeToggle.jsx'
import './VerifyOtp.css'

const CODE_LENGTH = 6
const RESEND_AFTER_S = 60

const mmss = total => {
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export default function VerifyOtp () {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { confirmCode, requestCode } = useAuth()
  const inputRef = useRef(null)

  const phone = state?.phone ?? null
  const [verificationId, setVerificationId] = useState(state?.verificationId ?? null)

  /*
   * The code itself, when there is no SMS provider.
   *
   * The server sends this only in development and only while OTP delivery
   * is going to its log — see the note on devCode in routes/auth.js. In
   * any build with a real provider it is undefined and this whole panel
   * never renders, so there is nothing to remember to take out.
   */
  const [devCode, setDevCode] = useState(state?.devCode ?? null)

  const [code, setCode] = useState('')
  const [isChecking, setChecking] = useState(false)
  const [error, setError] = useState(null)
  const [secondsLeft, setSecondsLeft] = useState(RESEND_AFTER_S)

  // Reached directly, with no number to verify — send them back rather than
  // showing a screen that cannot work.
  useEffect(() => {
    if (!phone) navigate('/login', { replace: true })
  }, [phone, navigate])

  useEffect(() => {
    if (secondsLeft <= 0) return undefined
    const timer = setTimeout(() => setSecondsLeft(s => s - 1), 1000)
    return () => clearTimeout(timer)
  }, [secondsLeft])

  const submit = useCallback(async value => {
    setChecking(true)
    setError(null)
    try {
      const user = await confirmCode(verificationId, value)
      // A returning account already has a name; only a new one is sent round
      // the profile step.
      const isNew = !user?.displayName?.trim()
      navigate(isNew ? '/profile-setup' : '/chats', { replace: true })
    } catch (err) {
      setError(userMessage(err))
      setCode('')
      setChecking(false)
      inputRef.current?.focus()
    }
  }, [confirmCode, verificationId, navigate])

  function onChange (event) {
    const next = event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH)
    setCode(next)
    setError(null)
    // Submitting on the last digit saves a tap, and matches what people
    // expect from every other OTP screen they have used.
    if (next.length === CODE_LENGTH) submit(next)
  }

  async function resend () {
    if (secondsLeft > 0 || !phone) return
    setError(null)
    try {
      const result = await requestCode(phone)
      setVerificationId(result?.verificationId ?? verificationId)
      setDevCode(result?.devCode ?? null)
      setSecondsLeft(RESEND_AFTER_S)
      inputRef.current?.focus()
    } catch (err) {
      setError(userMessage(err))
    }
  }

  if (!phone) return null

  const pretty = formatDisplay(phone)

  return (
    <main className="otp">
      <ThemeToggle className="otp__theme" />

      <div className="otp__card">
        <h1 className="otp__title">Verify {pretty}</h1>

        <p className="otp__sub">
          {devCode
            ? <>No SMS provider is connected yet, so the code is shown below
                instead of sent to <strong>{pretty}</strong>.{' '}</>
            : <>Waiting to automatically detect an SMS sent to <strong>{pretty}</strong>.{' '}</>}
          <Link className="otp__wrong" to="/login">Wrong number?</Link>
        </p>

        {devCode && (
          <button
            type="button"
            className="otp__devcode"
            onClick={() => { setCode(devCode); submit(devCode) }}
            data-testid="dev-code"
          >
            <span className="otp__devcode-label">Your code — tap to use</span>
            <span className="otp__devcode-value">{devCode}</span>
          </button>
        )}

        <label className="otp__field">
          <span className="ziv-sr-only">6-digit verification code</span>
          <input
            ref={inputRef}
            className="otp__input"
            type="text"
            inputMode="numeric"
            // What lets the platform drop the code in from the SMS itself.
            autoComplete="one-time-code"
            maxLength={CODE_LENGTH}
            value={code}
            onChange={onChange}
            disabled={isChecking}
            autoFocus
            aria-describedby="otp-hint"
          />
        </label>

        <p className="otp__hint" id="otp-hint">
          {isChecking ? 'Checking…' : `Enter ${CODE_LENGTH}-digit code`}
        </p>

        {error && <p className="otp__error" role="alert">{error}</p>}

        <div className="otp__actions">
          <button
            type="button"
            className="otp__resend"
            onClick={resend}
            disabled={secondsLeft > 0}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 4h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-5 4v-4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
                stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
            Resend SMS
            {secondsLeft > 0 && <span className="otp__timer">{mmss(secondsLeft)}</span>}
          </button>

          <hr className="otp__rule" />

          <button type="button" className="otp__call" onClick={resend} disabled={secondsLeft > 0}>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6.6 3h3l1.5 4-2.2 1.6a12 12 0 0 0 5.5 5.5L16 11.9l4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.6 5.2 2 2 0 0 1 6.6 3Z"
                stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
            Call me
          </button>
        </div>
      </div>
    </main>
  )
}
